"use client";

import { useState, useEffect, useLayoutEffect, useRef, useMemo } from "react";
import { useRouter } from "next/navigation";
import { useQuery } from "@tanstack/react-query";
import api from "@/client/api/axios";
import { useCityByNameQuery } from "@/hooks/useCities";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import Link from "next/link";
import Script from "next/script";
import Image from "next/image";
import { Star, MapPin, Heart, ChevronLeft, ChevronRight, ImageOff } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

import { Navbar } from "@/components/Navbar";
import { AdminListSearch } from "@/components/admin/AdminListSearch";
import { HeroLocationSelect } from "@/components/landing/HeroLocationSelect";
import { LazyTestimonials } from "@/components/landing/LazyTestimonials";
import { SiteFooter } from "@/components/landing/SiteFooter";
import { slugify, getCityNameForSearch } from "@/lib/utils";
import { getGoogleMapsScriptUrl, isGoogleMapsLoaded, shouldInjectGoogleMapsScript } from "@/lib/google-maps-loader";
import { toast } from "sonner";

const CAROUSEL_INTERVAL_MS = 5000;
const CARDS_PER_ROW = 3;

type LandingCategory = {
  id: number;
  name: string;
  bannerImageWeb: string | null;
  businessGroupId: number;
  businessGroup?: { id: number; name: string };
};

type BusinessGroupLanding = {
  id: number;
  name: string;
  sortOrder: number;
};

type TestimonialItem = {
  name: string;
  role: string;
  rating: number;
  ratingValue?: number;
  jobs?: number;
  location?: string;
  comment: string;
  image: string;
  reviews?: string[];
  featured?: boolean;
};

const TESTIMONIALS: TestimonialItem[] = [
  {
    name: "Jerry Watkins",
    role: "Property Manager",
    rating: 5,
    ratingValue: 4.6,
    jobs: 153,
    location: "New York, NY",
    comment:
      "VendorCall has made managing properties incredibly easy. The vendors are reliable and professional.",
    image:
      "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop",
    reviews: [
      "Lossette is generous, kind and very hard working. A pleasure to have in our home.",
      "Such an amazing job — I don't know that my apartment has ever been this clean. Thank you!",
    ],
    featured: true,
  },
  {
    name: "Sarah Johnson",
    role: "Vendor",
    rating: 5,
    ratingValue: 4.8,
    jobs: 89,
    location: "Austin, TX",
    comment:
      "Great platform to find consistent work. Customer base is professional and pays on time.",
    image:
      "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop",
    reviews: [
      "Elena is on time, organized, thorough and very clean. Would book with her again!",
      "Professional from start to finish. Highly recommend for any property manager.",
    ],
    featured: false,
  },
  {
    name: "Ronald Frazier",
    role: "Property Owner",
    rating: 5,
    ratingValue: 4.8,
    jobs: 215,
    location: "Inglewood, CA",
    comment:
      "Excellent service from start to finish. Would definitely use VendorCall again!",
    image:
      "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop",
    reviews: [
      "The best in the business. There is no one I trust more for assembly and property expertise.",
      "Reliable, skilled, and always on time. Five stars from a repeat customer.",
    ],
    featured: false,
  },
];

const TESTIMONIAL_REVIEW_COUNT = "2.8K";

function getCityFromGeocodeResult(result: google.maps.GeocoderResult): string {
  const addr = result.address_components ?? [];
  let cityName = "";
  for (const c of addr) {
    if (c.types.includes("locality")) {
      cityName = c.long_name;
      break;
    }
    if (c.types.includes("administrative_area_level_1") && !cityName) {
      cityName = c.long_name;
    }
  }
  return cityName || (result.formatted_address ?? "");
}

function getBookingHref(city: string, serviceTitle: string) {
  return `/booking/${encodeURIComponent(city)}/${slugify(serviceTitle)}`;
}

/** Display name for messaging: city only (wiring is by city, not country). */
function getCityDisplayName(location: string): string {
  const trimmed = location?.trim() ?? "";
  const commaIndex = trimmed.indexOf(",");
  return commaIndex > 0 ? trimmed.slice(0, commaIndex).trim() : trimmed;
}

export default function Home() {
  const router = useRouter();
  const [selectedLocation, setSelectedLocation] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchFilter, setSearchFilter] = useState("");
  const [sectionIndices, setSectionIndices] = useState<Record<number, number>>({});
  const [mapsReady, setMapsReady] = useState(
    () => typeof window !== "undefined" && isGoogleMapsLoaded()
  );
  const [categoryImageErrorIds, setCategoryImageErrorIds] = useState<Set<number>>(new Set());
  const [locationLoading, setLocationLoading] = useState(false);
  const pendingGeocodeRef = useRef<{ lat: number; lng: number } | null>(null);

  const googleMapsApiKey =
    typeof window !== "undefined"
      ? (process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY ?? "").trim()
      : (process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY ?? "").trim();
  const injectMapsScript = !!googleMapsApiKey && shouldInjectGoogleMapsScript();

  const {
    data: citiesData,
    isLoading: citiesLoading,
    isError: citiesError,
    refetch: refetchCities,
  } = useQuery({
    queryKey: ["cities", "name", selectedLocation],
    queryFn: async () => {
      const { data } = await api.get<{
        success: boolean;
        data: { id: number; name: string }[];
      }>("/cities", {
        params: { name: selectedLocation, status: "active" },
      });
      return data;
    },
    enabled: !!selectedLocation?.trim(),
  });

  const cityId = citiesData?.data?.[0]?.id;
  const resolvedCityName = citiesData?.data?.[0]?.name ?? null;

  const {
    data: categoriesData,
    isLoading: categoriesLoading,
    isError: categoriesError,
    refetch: refetchCategories,
  } = useQuery({
    queryKey: ["categories", "landing", cityId],
    queryFn: async () => {
      const { data } = await api.get<{
        success: boolean;
        data: LandingCategory[];
      }>("/categories", {
        params: { cityId, status: "ACTIVE", hasCityServices: "true" },
      });
      return data;
    },
    enabled: !!cityId,
  });

  const { data: businessGroupsData, isLoading: businessGroupsLoading } =
    useQuery({
      queryKey: ["business-groups", "landing"],
      queryFn: async () => {
        const { data } = await api.get<{
          success: boolean;
          data: BusinessGroupLanding[];
        }>("/business-groups", {
          params: { status: "active" },
        });
        return data;
      },
      enabled: !!selectedLocation?.trim(),
    });

  const categoriesByBusinessGroup = useMemo(() => {
    const list = (categoriesData?.data ?? []) as LandingCategory[];
    const map: Record<number, LandingCategory[]> = {};
    for (const c of list) {
      const bgId = c.businessGroup?.id ?? c.businessGroupId;
      if (!map[bgId]) map[bgId] = [];
      map[bgId].push(c);
    }
    return map;
  }, [categoriesData?.data]);

  const orderedSections = useMemo(() => {
    const groups = (businessGroupsData?.data ?? []) as BusinessGroupLanding[];
    return groups
      .filter((bg) => {
        const cats = categoriesByBusinessGroup[bg.id] ?? [];
        return cats.length > 0;
      })
      .map((bg) => ({
        group: bg,
        categories: categoriesByBusinessGroup[bg.id] ?? [],
      }));
  }, [businessGroupsData?.data, categoriesByBusinessGroup]);

  const showServicesByCategoryHeading =
    citiesLoading ||
    (!!cityId && (categoriesLoading || businessGroupsLoading)) ||
    (!!cityId && !categoriesLoading && !categoriesError && orderedSections.length > 0);

  const dbServiceTitles = useMemo(
    () => (categoriesData?.data ?? []).map((c) => c.name),
    [categoriesData?.data]
  );

  const handleUseMyLocation = () => {
    if (
      typeof window === "undefined" ||
      !window.navigator?.geolocation
    ) {
      toast.error("Location is not supported in this browser.");
      return;
    }
    setLocationLoading(true);
    window.navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setLocationLoading(false);
        if (window.google?.maps?.Geocoder) {
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode(
            { location: { lat: latitude, lng: longitude } },
            (results, status) => {
              if (status !== "OK" || !results?.[0]) return;
              const cityName = getCityFromGeocodeResult(results[0]);
              if (cityName) setSelectedLocation(getCityNameForSearch(cityName));
            }
          );
        } else {
          pendingGeocodeRef.current = { lat: latitude, lng: longitude };
        }
      },
      () => {
        setLocationLoading(false);
        toast.error("We couldn't get your location. Please enter your city above.");
      }
    );
  };

  useEffect(() => {
    if (
      !mapsReady ||
      !window.google?.maps?.Geocoder ||
      !pendingGeocodeRef.current
    ) {
      return;
    }
    const { lat, lng } = pendingGeocodeRef.current;
    pendingGeocodeRef.current = null;
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode(
      { location: { lat, lng } },
      (results, status) => {
        if (status !== "OK" || !results?.[0]) return;
        const cityName = getCityFromGeocodeResult(results[0]);
        if (cityName) setSelectedLocation(getCityNameForSearch(cityName));
      }
    );
  }, [mapsReady]);

  useLayoutEffect(() => {
    if (injectMapsScript && googleMapsApiKey && !isGoogleMapsLoaded()) {
      (window as unknown as Record<string, () => void>).__googleMapsReady = () => {
        if (isGoogleMapsLoaded()) {
          setMapsReady(true);
          delete (window as unknown as Record<string, unknown>).__googleMapsReady;
        }
      };
    }
  }, [injectMapsScript, googleMapsApiKey]);

  useEffect(() => {
    if (!googleMapsApiKey) return;
    if (isGoogleMapsLoaded()) return;
    let id: ReturnType<typeof setInterval> | null = null;
    const checkLoaded = () => {
      if (isGoogleMapsLoaded()) {
        setMapsReady(true);
        if (id) clearInterval(id);
      }
    };
    id = setInterval(checkLoaded, 100);
    return () => {
      if (id) clearInterval(id);
    };
  }, [googleMapsApiKey, injectMapsScript]);

  const serviceTitlesForCurrentCity = dbServiceTitles;

  const serviceSuggestions = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return [];
    return serviceTitlesForCurrentCity
      .filter((title) => title.toLowerCase().includes(q))
      .slice(0, 8);
  }, [serviceTitlesForCurrentCity, searchQuery]);

  useEffect(() => {
    queueMicrotask(() => setSectionIndices({}));
  }, [cityId, categoriesData?.data]);

  useEffect(() => {
    const t = setInterval(() => {
      setSectionIndices((prev) => {
        const next = { ...prev };
        for (const { group, categories } of orderedSections) {
          const maxIdx = Math.max(0, categories.length - CARDS_PER_ROW);
          if (maxIdx <= 0) continue;
          const current = prev[group.id] ?? 0;
          next[group.id] = current >= maxIdx ? 0 : current + 1;
        }
        return next;
      });
    }, CAROUSEL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [orderedSections]);

  return (
    <div className="min-h-screen bg-background">
      {googleMapsApiKey && injectMapsScript && (
        <Script
          src={getGoogleMapsScriptUrl(googleMapsApiKey)}
          strategy="afterInteractive"
          onLoad={() => setMapsReady(true)}
        />
      )}
      <Navbar />
      <section className="relative min-h-[520px] md:min-h-[680px] py-24 md:py-36 flex flex-col justify-center">
        <div className="absolute inset-0 z-0 overflow-hidden">
          <div className="absolute inset-0 bg-black/60 z-10" />
          <Image
            src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=1920&h=1080&fit=crop"
            alt="Background"
            fill
            sizes="100vw"
            className="object-cover"
          />
        </div>
        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-12">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-3">
              Find trusted vendors, the easy way
            </h1>
            <p className="mt-4 text-xs sm:text-sm font-medium uppercase tracking-[0.2em] text-white/70 max-w-2xl mx-auto">
              Connect with professional service providers in your area instantly
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-3 max-w-3xl mx-auto mb-6 h-12 items-stretch">
            <HeroLocationSelect
              value={selectedLocation}
              onValueChange={setSelectedLocation}
            />
            <div className="relative w-full min-h-0 h-12">
              <AdminListSearch
                value={searchQuery}
                onValueChange={setSearchQuery}
                onSearchChange={setSearchFilter}
                placeholder="Search for a service"
                size="hero"
                className="relative w-full h-full"
                inputClassName="h-full min-h-0 pl-10 text-sm md:text-base bg-white border-0 rounded-lg shadow-lg font-medium w-full placeholder-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary"
                onKeyDown={(e) => {
                  if (e.key !== "Enter") return;
                  e.preventDefault();
                  if (!selectedLocation?.trim()) {
                    toast.error("Please select a location first.");
                    return;
                  }
                  const q = searchQuery.trim().toLowerCase();
                  const match = q
                    ? serviceTitlesForCurrentCity.find((title) =>
                        title.toLowerCase().includes(q)
                      )
                    : null;
                  if (match) {
                    router.push(getBookingHref(resolvedCityName ?? selectedLocation, match));
                  } else {
                    toast.error("No matching service found.");
                  }
                }}
              />
              {serviceSuggestions.length > 0 && (
                <ul
                  className="absolute top-full left-0 right-0 z-[100] mt-1 max-h-60 overflow-auto rounded-lg border border-border bg-popover py-1 shadow-md"
                  role="listbox"
                >
                  {serviceSuggestions.map((title) => (
                    <li key={title}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={false}
                        className="w-full px-4 py-2.5 text-left text-sm text-popover-foreground hover:bg-accent hover:text-accent-foreground"
                        onClick={() => {
                          setSearchQuery("");
                          setSearchFilter("");
                          router.push(getBookingHref(resolvedCityName ?? selectedLocation, title));
                        }}
                      >
                        {title}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          <div className="flex justify-center mt-3">
            <button
              type="button"
              onClick={handleUseMyLocation}
              disabled={locationLoading}
              className="text-white/90 hover:text-white text-sm font-medium inline-flex items-center gap-1.5 transition disabled:opacity-60"
            >
              <MapPin className="w-4 h-4" />
              {locationLoading ? "Getting location…" : "Use my location"}
            </button>
          </div>
        </div>
      </section>

      {selectedLocation?.trim() && (
      <section className="py-16 md:py-24 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {showServicesByCategoryHeading && (
            <div className="text-center mb-14">
              <h2 className="text-3xl sm:text-4xl md:text-[2.75rem] font-bold text-foreground tracking-tight">
                Services By Category
              </h2>
              <p className="mt-4 text-xs sm:text-sm font-medium uppercase tracking-[0.2em] text-foreground/70">
                Browse recommended, on-demand, and resident services for your location.
              </p>
            </div>
          )}

          {citiesLoading ||
          (!!cityId && (categoriesLoading || businessGroupsLoading)) ? (
            <div className="space-y-16 md:space-y-20" aria-busy="true" aria-label="Loading services">
              {[1, 2, 3].map((block) => (
                <div key={block}>
                  <Skeleton className="h-8 w-64 mb-2" />
                  <Skeleton className="h-4 w-48 mb-6" />
                  <div className="flex items-stretch gap-4 md:gap-6">
                    <Skeleton className="h-10 w-10 rounded-full shrink-0 self-center" />
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 flex-1 min-w-0">
                      {[1, 2, 3].map((i) => (
                        <Card key={i} className="h-full overflow-hidden rounded-xl border border-border/80 p-0 gap-0 flex-col">
                          <Skeleton className="aspect-[3/2] w-full shrink-0 rounded-t-xl" />
                          <div className="p-4">
                            <Skeleton className="h-5 w-[75%]" />
                          </div>
                        </Card>
                      ))}
                    </div>
                    <Skeleton className="h-10 w-10 rounded-full shrink-0 self-center" />
                  </div>
                </div>
              ))}
            </div>
          ) : !citiesLoading &&
            !!selectedLocation?.trim() &&
            citiesData &&
            (citiesData.data?.length ?? 0) === 0 ? (
            <div className="text-center py-12 text-foreground/80">
              <p className="text-lg font-medium">
                We're not in <strong>{getCityDisplayName(selectedLocation)}</strong> yet.
              </p>
            </div>
          ) : (citiesError && !!selectedLocation?.trim()) ||
            (categoriesError && !!cityId) ? (
            <div className="text-center py-12">
              <p className="text-foreground/80 mb-4">
                Something went wrong loading services. Please try again.
              </p>
              <Button
                variant="outline"
                onClick={() => {
                  if (citiesError) refetchCities();
                  if (categoriesError && cityId) refetchCategories();
                }}
              >
                Try again
              </Button>
            </div>
          ) : !!cityId &&
            !categoriesLoading &&
            !categoriesError &&
            orderedSections.length === 0 ? (
            <div className="text-center py-12 text-foreground/80">
              <p className="text-lg font-medium">
                No services configured for this location.
              </p>
            </div>
          ) : (
            <div className="space-y-16 md:space-y-20">
              {orderedSections.map(({ group, categories: sectionCategories }) => {
                const idx = sectionIndices[group.id] ?? 0;
                const maxIdx = Math.max(
                  0,
                  sectionCategories.length - CARDS_PER_ROW
                );
                const slice = sectionCategories.slice(
                  idx,
                  idx + CARDS_PER_ROW
                );
                const showPrev = idx > 0;
                const showNext = idx < maxIdx;
                return (
                  <div key={group.id}>
                    <h3 className="text-xl md:text-2xl font-semibold text-foreground tracking-tight mb-2">
                      {group.name}
                    </h3>
                    <p className="text-sm text-foreground/60 mb-6">
                      Browse services for your location
                    </p>
                    <div className="flex items-stretch gap-4 md:gap-6">
                      <Button
                        variant="outline"
                        size="icon"
                        className="rounded-full h-10 w-10 shrink-0 border-2 shadow-sm hover:bg-muted self-center"
                        onClick={() =>
                          setSectionIndices((prev) => ({
                            ...prev,
                            [group.id]: Math.max(0, (prev[group.id] ?? 0) - 1),
                          }))
                        }
                        disabled={!showPrev}
                        aria-label={`Previous ${group.name}`}
                      >
                        <ChevronLeft className="h-5 w-5" />
                      </Button>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 flex-1 min-w-0">
                        {slice.map((category) => (
                          <Link
                            key={category.id}
                            href={getBookingHref(resolvedCityName ?? selectedLocation, category.name)}
                            className="group"
                          >
                            <Card className="h-full overflow-hidden rounded-xl border border-border/80 bg-card shadow-sm hover:shadow-md transition-all duration-200 p-0 gap-0 flex-col">
                              <div className="relative w-full overflow-hidden aspect-[3/2] bg-muted shrink-0 flex items-center justify-center">
                                {!category.bannerImageWeb?.trim() || categoryImageErrorIds.has(category.id) ? (
                                  <div className="flex flex-col items-center justify-center gap-2 p-4 text-center">
                                    <ImageOff className="h-8 w-8 text-muted-foreground" aria-hidden />
                                    <span className="text-xs text-muted-foreground line-clamp-2 px-2">
                                      {category.name}
                                    </span>
                                  </div>
                                ) : (
                                  // eslint-disable-next-line @next/next/no-img-element -- bannerImageWeb is dynamic (any host); next/image requires known remotePatterns
                                  <img
                                    src={category.bannerImageWeb}
                                    alt={category.name}
                                    className="absolute inset-0 size-full object-cover object-center group-hover:scale-105 transition duration-300"
                                    onError={() => {
                                      setCategoryImageErrorIds((prev) => new Set(prev).add(category.id));
                                    }}
                                  />
                                )}
                              </div>
                              <div className="p-4">
                                <p className="font-medium text-foreground line-clamp-2 group-hover:text-primary transition-colors">
                                  {category.name}
                                </p>
                              </div>
                            </Card>
                          </Link>
                        ))}
                      </div>
                      <Button
                        variant="outline"
                        size="icon"
                        className="rounded-full h-10 w-10 shrink-0 border-2 shadow-sm hover:bg-muted self-center"
                        onClick={() =>
                          setSectionIndices((prev) => ({
                            ...prev,
                            [group.id]: Math.min(
                              maxIdx,
                              (prev[group.id] ?? 0) + 1
                            ),
                          }))
                        }
                        disabled={!showNext}
                        aria-label={`Next ${group.name}`}
                      >
                        <ChevronRight className="h-5 w-5" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>
      )}

      <section className="py-16 md:py-24 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl md:text-[2.75rem] font-bold text-foreground tracking-tight">
              How It Works
            </h2>
            <p className="mt-4 text-xs sm:text-sm font-medium uppercase tracking-[0.2em] text-foreground/70">
              Three simple steps
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 md:gap-8 relative">
            <div
              className="hidden md:block absolute top-[4.5rem] left-[16.666%] right-[16.666%] h-0.5 bg-gradient-to-r from-transparent via-primary/30 to-transparent"
              aria-hidden
            />
            {[
              {
                title: "Find",
                description:
                  "Quickly search pre-vetted vendors or service providers that already have connections in the local industry",
              },
              {
                title: "Evaluate",
                description:
                  "VendorCall platform will allow you to see their skills, compliance, average rates, and more information",
              },
              {
                title: "Schedule",
                description:
                  "You can request a proposal, schedule a service, or get immediate help to find your perfect vendor match",
              },
            ].map((step, i) => (
              <div key={i} className="relative">
                <div className="h-full rounded-xl border border-border/80 bg-card p-6 md:p-8 shadow-sm hover:shadow-md transition-shadow duration-200 text-center">
                  <div className="relative">
                    <div className="w-16 h-16 md:w-20 md:h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto border-2 border-primary/30 shadow-inner mb-5">
                      <span className="text-2xl md:text-3xl font-bold text-primary">
                        {i + 1}
                      </span>
                    </div>
                    <h3 className="text-xl font-semibold text-foreground mb-3">
                      {step.title}
                    </h3>
                    <p className="text-sm text-foreground/75 leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-50/80 via-background to-slate-50/60" aria-hidden />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,oklch(0.486_0.15_259_/_.08),transparent)]" aria-hidden />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl md:text-[2.75rem] font-bold text-foreground tracking-tight">
              Our Customers Say the Nicest Things
            </h2>
            <p className="mt-4 text-xs sm:text-sm font-medium uppercase tracking-[0.2em] text-foreground/60">
              Tap into new opportunities
            </p>
          </div>

          <LazyTestimonials staticFallback={TESTIMONIALS} staticReviewCount={TESTIMONIAL_REVIEW_COUNT} />
        </div>
      </section>

      <section className="py-16 md:py-24 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl md:text-[2.75rem] font-bold text-foreground tracking-tight">
              Grow Your Local Business With VendorCall
            </h2>
            <p className="mt-4 text-xs sm:text-sm font-medium uppercase tracking-[0.2em] text-foreground/70">
              Start receiving jobs and growing your business
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {[
              {
                title: "Complete your profile",
                points: [
                  "Sign up for an account today",
                  "Verify your business",
                  "Create your profile",
                  "Upload documents",
                ],
              },
              {
                title: "Get approved",
                points: [
                  "Upload business documents",
                  "Get background checked",
                  "Add services",
                ],
              },
              {
                title: "Receive jobs and get paid",
                points: [
                  "Get matched with relevant jobs",
                  "Direct communication with properties",
                  "Secure billing and payment",
                  "Fast payouts within 2 hours",
                ],
              },
            ].map((benefit, i) => (
              <Card
                key={i}
                className="relative overflow-hidden border border-border/80 bg-card shadow-sm hover:shadow-md transition-shadow duration-200 p-6 md:p-8 rounded-xl"
              >
                <span
                  className="absolute top-5 right-5 text-5xl font-bold text-foreground/10 select-none tabular-nums"
                  aria-hidden
                >
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div className="relative">
                  <span className="inline-block text-xs font-semibold uppercase tracking-wider text-primary mb-3">
                    Step {i + 1}
                  </span>
                  <h3 className="text-xl font-semibold text-foreground mb-5 pr-8">
                    {benefit.title}
                  </h3>
                  <ul className="space-y-3">
                    {benefit.points.map((point, j) => (
                      <li
                        key={j}
                        className="text-sm text-foreground/80 pl-4 border-l-2 border-primary/20"
                      >
                        {point}
                      </li>
                    ))}
                  </ul>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden py-12 md:py-16">
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-xl border border-white/20 bg-gradient-to-br from-primary via-primary to-primary/90 text-primary-foreground p-5 md:p-7 text-center shadow-xl">
            <p className="text-primary-foreground/90 text-xs font-semibold uppercase tracking-[0.15em] mb-2">
              For service professionals
            </p>
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold tracking-tight leading-tight mb-5 max-w-2xl mx-auto">
              Are you a professional looking to grow your business?
            </h2>

            <div className="flex justify-center">
              <Link href="/vendor-register">
                <Button
                  size="default"
                  className="bg-white text-primary hover:bg-white/95 hover:shadow-lg active:scale-[0.98] transition-all duration-200 font-semibold px-6 py-3 text-sm rounded-lg shadow-md"
                >
                  Become A Pro
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
